<template>
  <a-space direction="vertical" style="width:100%" size="middle">
    <a-card :bordered="false">
      <a-space wrap>
        <a-input
          v-model:value="search"
          placeholder="Поиск по ФИО, комментарию, заказчику"
          style="width:320px"
          allowClear
        />
        <a-select
          v-model:value="contractorFilter"
          :options="contractorFilterOptions"
          style="width:280px"
        />
        <a-segmented v-model:value="activeFilter" :options="activeFilterOptions" />
        <a-button @click="load" :loading="loading">Обновить</a-button>
      </a-space>
    </a-card>

    <a-card title="Дети" :bordered="false">
      <a-table
        :dataSource="filteredRows"
        :columns="columns"
        :loading="loading"
        :pagination="false"
        rowKey="row_key"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'tags'">
            <ChildTagIcons :tags="record.tags" />
          </template>

          <template v-else-if="column.key === 'active'">
            <a-tag :color="record.active ? 'green' : 'default'">
              {{ record.active ? 'Да' : 'Нет' }}
            </a-tag>
          </template>
        </template>
      </a-table>
    </a-card>
  </a-space>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { message } from 'ant-design-vue'
import { getKitchenContractors, getKitchenContractorChildren } from '../../api/contractors'
import { getKitchenTags } from '../../api/tags'
import ChildTagIcons from '../../components/ChildTagIcons.vue'

const loading = ref(false)
const rows = ref([])
const contractors = ref([])
const tags = ref([])
const search = ref('')
const contractorFilter = ref('ALL')
const activeFilter = ref('ALL')

const activeFilterOptions = [
  { value: 'ALL', label: 'Все' },
  { value: 'ACTIVE', label: 'Активные' },
  { value: 'INACTIVE', label: 'Неактивные' },
]

const columns = [
  { title: 'ФИО', dataIndex: 'full_name' },
  { title: 'Заказчик', dataIndex: 'contractor_name', width: 260 },
  { title: 'Теги', key: 'tags', width: 120 },
  { title: 'Комментарий', dataIndex: 'comment' },
  { title: 'Активен', key: 'active', width: 110 },
]

const contractorFilterOptions = computed(() => [
  { value: 'ALL', label: 'Все заказчики' },
  ...contractors.value.map(item => ({
    value: String(item.id),
    label: item.name,
  })),
])

const tagMap = computed(() =>
  Object.fromEntries((tags.value || []).map(tag => [String(tag.id), tag.name]))
)

const filteredRows = computed(() => {
  const query = search.value.trim().toLowerCase()

  return rows.value.filter(row => {
    if (contractorFilter.value !== 'ALL' && String(row.contractor_id) !== contractorFilter.value) {
      return false
    }

    if (activeFilter.value === 'ACTIVE' && !row.active) {
      return false
    }

    if (activeFilter.value === 'INACTIVE' && !!row.active) {
      return false
    }

    if (!query) return true

    const haystack = [
      row.full_name,
      row.comment,
      row.contractor_name,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()

    return haystack.includes(query)
  })
})

async function load() {
  loading.value = true
  try {
    const [contractorsRes, tagsRes] = await Promise.all([
      getKitchenContractors(),
      getKitchenTags(),
    ])

    contractors.value = (contractorsRes.data.data || []).sort((a, b) => a.name.localeCompare(b.name))
    tags.value = tagsRes.data.data || []

    const childrenResults = await Promise.all(
      contractors.value.map(async contractor => {
        const childrenRes = await getKitchenContractorChildren(contractor.id)
        const children = childrenRes.data.data || []

        return children.map(child => ({
          ...child,
          row_key: `${contractor.id}-${child.id}`,
          contractor_id: contractor.id,
          contractor_name: contractor.name,
          tags: (child.tag_ids || []).map(tagId => tagMap.value[String(tagId)]).filter(Boolean),
          active: !!child.active,
        }))
      })
    )

    rows.value = childrenResults.flat().sort((a, b) => a.full_name.localeCompare(b.full_name))
  } catch (e) {
    message.error(e?.response?.data?.error?.message || 'Ошибка загрузки')
  } finally {
    loading.value = false
  }
}

onMounted(load)
</script>
